import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth.ts';
import { isLocal } from '../config/stage.ts';
import { injectTermly, removeTermly } from './cmp.ts';

/** Route hosting the dedicated cookie-preferences page. */
const COOKIE_PREFERENCES_PATH = '/legal/cookie-preferences';

/**
 * Loads the Termly CMP conditionally, and ONLY where we allow it:
 *   1. the marketing home page (`/`) for signed-out visitors — the consent
 *      banner for new users; or
 *   2. the dedicated cookie-preferences page (any auth state) — so signed-in
 *      users can still review or withdraw their choices.
 *
 * Everywhere else the script is torn down (best-effort) so the banner never
 * lingers after a client-side navigation. This is deliberately NOT a static
 * `<script>` in index.html (that would load on every route). Renders nothing.
 *
 * `status === 'initializing'` is treated as inactive so the banner doesn't flash
 * before auth resolves — and signed-in users are redirected off `/` anyway
 * (see HomeRoute), so `/` is naturally the signed-out landing.
 *
 * Termly never loads on the `local` stage (don't hit a real CMP from localhost)
 * nor in automated browsers (`navigator.webdriver`, i.e. Playwright) — that
 * keeps dev and the e2e suite deterministic, since real users on beta/prod still
 * get the banner. See `docs/privacy-consent.md` for the full rationale.
 */
export default function TermlyLoader(): null {
  const { pathname } = useLocation();
  const { status } = useAuth();

  const automated =
    typeof navigator !== 'undefined' && navigator.webdriver;
  const active =
    !isLocal &&
    !automated &&
    ((pathname === '/' && status === 'unauthenticated') ||
      pathname === COOKIE_PREFERENCES_PATH);

  useEffect(() => {
    if (active) {
      injectTermly();
    } else {
      removeTermly();
    }
  }, [active]);

  return null;
}
